import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, ChevronDown, ShieldCheck, FileText, Eye, Download, ScanLine, ArrowRight } from "lucide-react";
import api, { API } from "@/lib/apiClient";
import SEO from "@/components/SEO";

const PAGE = 12;
const STATUS_CLS = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SUPERSEDED: "bg-amber-50 text-amber-700 border-amber-200",
  WITHDRAWN: "bg-red-50 text-red-600 border-red-200",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-200",
};
const CHIP_CATS = ["Office Memorandum", "Office Order", "Notification", "Circular", "Public Notice"];
const fmtDate = (iso) => {
  const d = new Date((iso || "") + "T00:00:00");
  return isNaN(d) ? (iso || "—") : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
};
const EMPTY = { q: "", category: "", year: "", authority: "", status: "", from: "", to: "", sort: "newest" };

export default function OfficialDocuments() {
  const [f, setF] = useState(EMPTY);
  const [dq, setDq] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ categories: [], authorities: [], years: [] });
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { const t = setTimeout(() => setDq(f.q), 350); return () => clearTimeout(t); }, [f.q]);

  const params = useCallback((skipPage) => {
    const p = new URLSearchParams({ sort: f.sort, limit: String(PAGE), page: String(skipPage) });
    if (dq.trim()) p.set("q", dq.trim());
    if (f.category) p.set("category", f.category);
    if (f.year) p.set("year", f.year);
    if (f.authority) p.set("authority", f.authority);
    if (f.status) p.set("status", f.status);
    if (f.from) p.set("date_from", f.from);
    if (f.to) p.set("date_to", f.to);
    return p.toString();
  }, [f, dq]);

  const load = useCallback(async (page, reset) => {
    reset ? setLoading(true) : setMore(true);
    try {
      const { data } = await api.get(`/official-documents?${params(page)}`);
      setTotal(data.total || 0);
      setFacets(data.facets || { categories: [], authorities: [], years: [] });
      setStatuses(data.statuses || []);
      setItems((prev) => reset ? (data.items || []) : [...prev, ...(data.items || [])]);
    } catch { if (reset) { setItems([]); setTotal(0); } }
    finally { setLoading(false); setMore(false); }
  }, [params]);

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); load(1, true); }, [load]);
  const loadMore = () => { const n = page + 1; setPage(n); load(n, false); };

  const hasFilters = f.q || f.category || f.year || f.authority || f.status || f.from || f.to || f.sort !== "newest";
  const clear = () => { setF(EMPTY); setDq(""); };
  const inp = "w-full border border-npst-line rounded-md bg-white px-3 py-2.5 text-sm text-npst-ink outline-none focus:border-npst-gold";

  return (
    <div className="fade-in">
      <SEO title="Official Documents — NPST" description="Searchable, verifiable public repository of official documents, orders, notifications and circulars issued by the National Public Service Trust." canonical={`${window.location.origin}/official-documents`} />
      <section className="border-b border-npst-line paper-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-2 label-eyebrow text-npst-gold mb-3"><ShieldCheck size={14} /> Verifiable Public Records</div>
          <h1 className="font-serif text-4xl sm:text-6xl font-semibold text-npst-ink tracking-tight">Official Documents</h1>
          <p className="mt-4 text-npst-inkSoft max-w-2xl leading-relaxed">A permanent, searchable repository of official documents issued by the Trust. Every document carries a unique NPST Document ID and can be independently verified.</p>
          <Link to="/verify" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy link-underline" data-testid="verify-a-document-link">
            <ScanLine size={15} /> Verify a Document
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* FILTERS */}
        <div className="bg-white border border-npst-line rounded-md p-4 sm:p-5 shadow-sm" data-testid="docs-filter-bar">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative lg:col-span-2">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
              <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search by title, Document ID, reference no., authority, keyword…" className={`${inp} pl-9`} data-testid="docs-search-input" />
            </div>
            <div className="relative">
              <select value={f.authority} onChange={(e) => set("authority", e.target.value)} className={`${inp} appearance-none pr-8 cursor-pointer`} data-testid="docs-authority-select">
                <option value="">All Authorities</option>
                {facets.authorities.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            </div>
            <div className="relative">
              <select value={f.sort} onChange={(e) => set("sort", e.target.value)} className={`${inp} appearance-none pr-8 cursor-pointer`} data-testid="docs-sort-select">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="date">Document Date</option>
                <option value="recent">Recently Uploaded</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            </div>
            <div className="relative">
              <select value={f.year} onChange={(e) => set("year", e.target.value)} className={`${inp} appearance-none pr-8 cursor-pointer`} data-testid="docs-year-select">
                <option value="">All Years</option>
                {facets.years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            </div>
            <div className="relative">
              <select value={f.status} onChange={(e) => set("status", e.target.value)} className={`${inp} appearance-none pr-8 cursor-pointer`} data-testid="docs-status-select">
                <option value="">Any Status</option>
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            </div>
            <div className="flex gap-2">
              <input type="date" value={f.from} onChange={(e) => set("from", e.target.value)} className={inp} data-testid="docs-date-from" aria-label="From date" />
              <input type="date" value={f.to} onChange={(e) => set("to", e.target.value)} className={inp} data-testid="docs-date-to" aria-label="To date" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-npst-line/60">
            <div className="text-xs uppercase tracking-wider text-npst-muted" data-testid="docs-count">{loading ? "Loading…" : `Showing ${items.length} of ${total} document${total !== 1 ? "s" : ""}`}</div>
            {hasFilters && <button onClick={clear} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-npst-muted hover:text-npst-navy px-3 py-2" data-testid="docs-clear-filters"><X size={13} /> Clear Filters</button>}
          </div>
        </div>

        {/* CATEGORY CHIPS */}
        <div className="flex flex-wrap gap-2 mt-6" data-testid="docs-category-chips">
          <button onClick={() => set("category", "")} className={`px-4 py-2 rounded-full border text-sm transition-colors ${!f.category ? "bg-npst-navy text-white border-npst-navy" : "border-npst-line bg-white text-npst-ink hover:border-npst-gold"}`} data-testid="cat-chip-all">All</button>
          {CHIP_CATS.map((c) => (
            <button key={c} onClick={() => set("category", f.category === c ? "" : c)} className={`px-4 py-2 rounded-full border text-sm transition-colors ${f.category === c ? "bg-npst-navy text-white border-npst-navy" : "border-npst-line bg-white text-npst-ink hover:border-npst-gold"}`} data-testid="cat-chip">{c}</button>
          ))}
          <button onClick={() => set("category", "Other Official Document")} className={`px-4 py-2 rounded-full border text-sm transition-colors ${f.category === "Other Official Document" ? "bg-npst-navy text-white border-npst-navy" : "border-npst-line bg-white text-npst-ink hover:border-npst-gold"}`} data-testid="cat-chip">Others</button>
        </div>

        {/* LIST */}
        <div className="mt-8">
          {loading ? <div className="py-20 text-center text-npst-muted animate-pulse">Loading documents…</div>
            : items.length === 0 ? (
              <div className="py-20 text-center" data-testid="docs-empty">
                <FileText size={36} className="text-npst-line mx-auto mb-3" />
                <p className="font-serif text-2xl text-npst-ink">No documents found</p>
                <p className="text-npst-muted mt-2 text-sm">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="docs-list">
                {items.map((d) => (
                  <div key={d.doc_id} className="bg-white border border-npst-line rounded-md p-4 sm:p-5 hover:border-npst-gold transition-colors" data-testid="doc-row">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className="text-[11px] uppercase tracking-wider font-semibold text-npst-gold">{d.category}</span>
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_CLS[d.status] || STATUS_CLS.ARCHIVED}`} data-testid="doc-status">{d.status}</span>
                        </div>
                        <Link to={`/official-documents/${d.slug}`} className="font-serif text-lg sm:text-xl font-semibold text-npst-ink hover:text-npst-navy transition-colors block leading-snug" data-testid="doc-title-link">{d.title}</Link>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-npst-muted">
                          {d.reference_no && <span><span className="uppercase tracking-wider">Ref:</span> <span className="text-npst-inkSoft font-medium">{d.reference_no}</span></span>}
                          <span><span className="uppercase tracking-wider">Date:</span> <span className="text-npst-inkSoft font-medium">{fmtDate(d.doc_date)}</span></span>
                          {d.authority && <span><span className="uppercase tracking-wider">By:</span> <span className="text-npst-inkSoft font-medium">{d.authority}</span></span>}
                          <span className="font-mono text-[11px] text-npst-navy">{d.doc_id}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <Link to={`/official-documents/${d.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-npst-line rounded-sm text-npst-navy hover:border-npst-gold transition-colors" data-testid="doc-view-details"><Eye size={13} /> Details</Link>
                        <a href={`${API}/official-documents/download/${d.doc_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-npst-line rounded-sm text-npst-navy hover:border-npst-gold transition-colors" data-testid="doc-view-pdf"><FileText size={13} /> PDF</a>
                        <Link to={`/verify/${d.doc_id}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider bg-npst-navy text-white rounded-sm hover:bg-npst-navyDeep transition-colors" data-testid="doc-verify"><ShieldCheck size={13} /> Verify</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          {!loading && items.length < total && (
            <div className="mt-10 text-center">
              <button onClick={loadMore} disabled={more} className="inline-flex items-center gap-2 px-7 py-3.5 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navy hover:text-white transition-colors disabled:opacity-50" data-testid="docs-load-more">{more ? "Loading…" : "Load More Documents"}</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
